/**
 * Utilities for mapping input source IDs to mate IDs.
 *
 * This module provides the mapping that allows games to use abstract source IDs
 * (like 'mouse', 'gamepad-0') while the room system works with mate IDs.
 */

/** Identifies an input source (controller, mouse, etc.) */
type SourceId = string;

/**
 * Build the sourceToMateId map from room state.
 *
 * This maps input source IDs (like 'mouse', 'gamepad-0') to mate IDs
 * so we can attribute moves correctly.
 */
export function buildSourceToMateIdMap(input: {
  isSharedScreen: boolean;
  viewingMateId?: string;
  sharedScreenFallbackMateId?: string;
  localMates: Array<{ id: string }>;
  roomMates: Array<{ mateId?: string; controllerId?: string }>;
}): Map<SourceId, string> {
  const {
    isSharedScreen,
    viewingMateId,
    sharedScreenFallbackMateId,
    localMates,
    roomMates,
  } = input;
  const map = new Map<SourceId, string>();
  const defaultMateId = isSharedScreen ? sharedScreenFallbackMateId : viewingMateId;

  // Always map 'mouse' to viewing mate if available
  // This allows mouse clicks to work in any mode
  if (defaultMateId) {
    map.set('mouse', defaultMateId);
  }

  // Also map keyboard 'K' to viewing mate
  if (defaultMateId) {
    map.set('K', defaultMateId);
  }

  // Non-shared (single/test) mode: allow gamepad-style sources to act as the viewing mate.
  //
  // This is important when keyboard input is routed into an emulated controller (X1/X2/...)
  // or when a real gamepad is used in single mode. Without this mapping, moves are dropped
  // because they can't be attributed to any mate.
  if (!isSharedScreen && viewingMateId) {
    for (let i = 1; i <= 8; i++) {
      map.set(`X${i}`, viewingMateId);
    }
  }

  if (isSharedScreen) {
    // Shared mode: also map controller IDs to mate IDs
    for (const localMate of localMates) {
      const roomMate = roomMates.find(m => m.controllerId === localMate.id);
      if (roomMate?.mateId) {
        map.set(localMate.id, roomMate.mateId);
      }
    }
  }

  return map;
}
